import "./App.css";
import Home from "./pages/Home";
import About from "./pages/About";
import PrePhd from "./pages/PrePhd";
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";
import ProjectViewMore from "./components/ProjectViewMore";
import Education from "./pages/Education";
import MentalHealth from "./pages/MentalHealth";
import Admin from "./pages/Admin";
import { DarkModeProvider } from "./components/DarkModeContext";
import { StudioProvider } from "./studio/StudioContext";
import StudioChrome from "./studio/StudioChrome";
import { phdResearchProjects } from "./data/phdResearchProjects";
import { isProjectShown } from "./utils/projects";

/** Preserve query keys (view, pillar, studio, …) when sending old Research URLs to Home. */
const toHomeResearch = (params) => {
  const qs = params.toString();
  return qs ? `/home?${qs}` : "/home#research-agenda";
};

const ResearchRedirect = () => {
  const [params] = useSearchParams();
  return <Navigate to={toHomeResearch(params)} replace />;
};

const ResearchProjectRedirect = () => {
  const { id } = useParams();
  if (!id) return <Navigate to="/home?view=projects" replace />;
  const isPhd = phdResearchProjects.some(
    (project) => project.id === id && isProjectShown(project),
  );
  if (isPhd) {
    return (
      <Navigate to={`/home?view=projects&project=${id}`} replace />
    );
  }
  return <Navigate to={`/projects/${id}`} replace />;
};

const ResearchPillarRedirect = () => {
  const { id } = useParams();
  const next = new URLSearchParams();
  next.set("view", "agenda");
  if (id) next.set("pillar", id);
  return <Navigate to={toHomeResearch(next)} replace />;
};

const ProjectsListRedirect = () => {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("view", "projects");
  return <Navigate to={toHomeResearch(next)} replace />;
};

const ProjectDetailRoute = () => {
  const { id } = useParams();
  const isPhd = phdResearchProjects.some(
    (project) => project.id === id && isProjectShown(project),
  );
  if (isPhd) {
    return (
      <Navigate to={`/home?view=projects&project=${id}`} replace />
    );
  }
  return <ProjectViewMore />;
};

function App() {
  return (
    <DarkModeProvider>
      <StudioProvider>
        <div className="max-w-full bg-lightBg dark:bg-darkBg">
          <StudioChrome />
          <Routes>
            <Route exact path="" element={<Home />} />
            <Route exact path="home" element={<Home />} />
            <Route exact path="about" element={<About />} />
            <Route exact path="research" element={<ResearchRedirect />} />
            <Route path="research/:id" element={<ResearchProjectRedirect />} />
            <Route exact path="agenda/:id" element={<ResearchPillarRedirect />} />

            <Route exact path="projects" element={<ProjectsListRedirect />} />
            <Route exact path="projects/:id" element={<ProjectDetailRoute />} />
            <Route
              exact
              path="projects/undefined"
              element={
                <main className="h-screen p-2">
                  <p>There's nothing here!</p>
                </main>
              }
            />
            <Route exact path="pre-phd" element={<PrePhd />} />
            <Route
              exact
              path="past-life"
              element={<Navigate to="/pre-phd" replace />}
            />
            <Route
              exact
              path="work"
              element={<Navigate to="/pre-phd" replace />}
            />
            <Route exact path="education" element={<Education />} />
            <Route exact path="mental-health" element={<MentalHealth />} />
            {process.env.NODE_ENV === "development" && (
              <Route exact path="admin" element={<Admin />} />
            )}
            <Route
              path="*"
              element={
                <main className="h-screen p-2">
                  <p>There's nothing here!</p>
                </main>
              }
            />
          </Routes>
        </div>
      </StudioProvider>
    </DarkModeProvider>
  );
}

export default App;
